import { useState, FC } from 'react';
import clsx from 'clsx';
import { Box, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { Modal, Carousel } from '../../components';
import { PlayCircle } from '@mui/icons-material';
import { ICustomPageSection } from '../../models';

interface IYouTubeVideoSection {
  section: ICustomPageSection;
}
const SLIDES_TO_SHOW = 3;

export const YouTubeVideoSection: FC<IYouTubeVideoSection> = ({ section }) => {
  const [currentVideo, setCurrentVideo] = useState<{
    name: string;
    videoId: string;
  } | null>(null);
  const [currentIndex, setCurrentIndex] = useState(section?.sections?.length >= 3 ? Math.round(SLIDES_TO_SHOW * 0.5 + 1) : 0);
  const arrLength = section?.sections?.length;
  const classes = useStyles();
  return (
    section?.sections?.length > 0 && (
      <>
        <Carousel
          show={section?.sections?.length >= 3 ? SLIDES_TO_SHOW : section?.sections?.length ?? 1}
          currentItemIndex={currentIndex}
          infiniteLoop={section?.sections?.length > 3}
          handleCurrentIndex={index => {
            setCurrentIndex(index);
          }}
        >
          {section.sections.map((section, index) => {
            // take this url `https://www.youtube.com/embed/CBL4h1J_BCI?enablejsapi=1&version=3&autoplay=1&controls=0&rel=0&origin=https://www.stonemartinbuilders.com`
            // and then pull out the videoId
            const arr = section.linkUrl.split('?')[0].split('/');
            const videoId = arr[arr.length - 1];

            const activeSlide = () => {
              if (currentIndex >= 8) {
                return currentIndex - arrLength - Math.round(SLIDES_TO_SHOW * 0.5);
              } else if (currentIndex === 1) {
                return arrLength - 1;
              } else if (currentIndex === 0) {
                return arrLength - 2;
              } else {
                return currentIndex - Math.round(SLIDES_TO_SHOW * 0.5);
              }
            };
            return (
              <div key={`$${index}`} id={`video-${index}`}>
                <Box className={clsx(classes.item, index === activeSlide() ? classes.isActive : '')}>
                  <img className={classes.image} alt={section.name} src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} />
                  <Box className={classes.playButtonWrapper}>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        setCurrentVideo({
                          name: section.name,
                          videoId
                        })
                      }
                    >
                      <PlayCircle
                        sx={{
                          fontSize: {
                            xs: '4rem',
                            sm: '6rem',
                            lg: '7rem'
                          }
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </div>
            );
          })}
        </Carousel>
        {!!currentVideo && (
          <Modal
            open={true}
            onClose={() => {
              setCurrentVideo(null);
            }}
            maxWidth="md"
            showClose={false}
            noPaddingContent={true}
            sx={{
              backgroundColor: 'transparent'
            }}
          >
            <iframe
              title={currentVideo.name}
              src={`https://www.youtube.com/embed/${currentVideo.videoId}?enablejsapi=1&version=3&autoplay=1&controls=0&rel=0&origin=https://www.stonemartinbuilders.com`}
              id={`ytplayer-${currentVideo.videoId}`}
              width="100%"
              height="600"
              frameBorder="0"
              allow="autoplay"
            />
          </Modal>
        )}
      </>
    )
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  wrapper: {
    width: '100%',
    display: 'flex',
    position: 'relative'
  },
  contentWrapper: {
    width: '100%',
    height: '100%'
  },
  content: {
    display: 'flex',
    transition: 'all 250ms linear',
    scrollbarWidth: 'none'
  },
  item: {
    width: '100%',
    padding: 8,
    position: 'relative',
    flexShrink: 0,
    flexGrow: 1,
    opacity: 0.5,
    transition: '.3s opacity ease-in-out',
    '&:hover, &:focus': {
      opacity: 1
    }
  },
  isActive: {
    opacity: 1
  },
  prevArrow: {
    position: 'absolute',
    zIndex: 1,
    top: '50%',
    transform: `translateY(-50%)`,
    width: `48px`,
    height: '48px',
    borderRadius: '24px',
    backgroundColor: '#fff',
    border: `1px solid #fff`,
    left: 24,
    paddingLeft: '1rem',
    '&:hover': {
      backgroundColor: '#ddd'
    }
  },
  nextArrow: {
    position: 'absolute',
    zIndex: 1,
    top: '50%',
    transform: `translateY(-50%)`,
    width: `48px`,
    height: '48px',
    borderRadius: '24px',
    border: `1px solid #fff`,
    backgroundColor: '#fff',
    right: 24,
    '&:hover': {
      backgroundColor: '#ddd'
    }
  },
  playButtonWrapper: {
    width: '100%',
    top: '50%',
    transform: `translateY(-50%)`,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  image: {
    width: '100%'
  }
}));
